import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import api from '../services/ApiService';

// ── Static code databases ─────────────────────────────────────────────────────

const SIP_CODES: { code: number; name: string; desc: string; severity: 'info' | 'warn' | 'error' }[] = [
  // 1xx
  { code: 100, name: 'Trying',                  desc: 'Request received, processing.', severity: 'info' },
  { code: 180, name: 'Ringing',                 desc: 'Destination is ringing.', severity: 'info' },
  { code: 183, name: 'Session Progress',        desc: 'Early media / ringing with media.', severity: 'info' },
  // 2xx
  { code: 200, name: 'OK',                      desc: 'Request succeeded. Call answered or transaction complete.', severity: 'info' },
  { code: 202, name: 'Accepted',                desc: 'Request accepted for processing.', severity: 'info' },
  // 3xx
  { code: 301, name: 'Moved Permanently',       desc: 'User has moved — update the contact URI.', severity: 'warn' },
  { code: 302, name: 'Moved Temporarily',       desc: 'Try a different address (redirect).', severity: 'warn' },
  { code: 305, name: 'Use Proxy',               desc: 'Must use a specific proxy.', severity: 'warn' },
  // 4xx
  { code: 400, name: 'Bad Request',             desc: 'Malformed SIP message. Check headers / SDP.', severity: 'error' },
  { code: 401, name: 'Unauthorized',            desc: 'Authentication required (digest challenge from UAS).', severity: 'error' },
  { code: 403, name: 'Forbidden',               desc: 'Request refused — wrong credentials, blocked number, or insufficient permissions.', severity: 'error' },
  { code: 404, name: 'Not Found',               desc: 'User or destination does not exist on this server.', severity: 'error' },
  { code: 405, name: 'Method Not Allowed',      desc: 'Method not supported by this UA/server.', severity: 'error' },
  { code: 407, name: 'Proxy Auth Required',     desc: 'Proxy requires authentication. Check trunk credentials.', severity: 'error' },
  { code: 408, name: 'Request Timeout',         desc: 'Server did not receive response in time. Network/firewall issue?', severity: 'error' },
  { code: 410, name: 'Gone',                    desc: 'User existed but is no longer available.', severity: 'error' },
  { code: 415, name: 'Unsupported Media Type',  desc: 'Codec or content-type mismatch. Check allowed codecs.', severity: 'error' },
  { code: 416, name: 'Unsupported URI Scheme',  desc: 'Unsupported URI scheme (e.g. tel:).', severity: 'error' },
  { code: 420, name: 'Bad Extension',           desc: 'Server does not understand the Require header extension.', severity: 'error' },
  { code: 480, name: 'Temporarily Unavailable', desc: 'Extension exists but is not currently reachable (not registered, DND, etc.).', severity: 'error' },
  { code: 481, name: 'Call/Trans Does Not Exist',desc: 'Dialog or transaction has already been terminated.', severity: 'error' },
  { code: 482, name: 'Loop Detected',           desc: 'Routing loop detected in the SIP path.', severity: 'error' },
  { code: 483, name: 'Too Many Hops',           desc: 'Max-Forwards header reached 0. Routing loop or misconfigured proxy.', severity: 'error' },
  { code: 484, name: 'Address Incomplete',      desc: 'Incomplete destination number. Check dial rules.', severity: 'error' },
  { code: 485, name: 'Ambiguous',               desc: 'Destination is ambiguous.', severity: 'error' },
  { code: 486, name: 'Busy Here',               desc: 'Called party is busy. Normal busy signal.', severity: 'warn' },
  { code: 487, name: 'Request Terminated',      desc: 'Request was cancelled by caller (normal).', severity: 'info' },
  { code: 488, name: 'Not Acceptable Here',     desc: 'Codec/media negotiation failed. Check SDP/codec config.', severity: 'error' },
  { code: 491, name: 'Request Pending',         desc: 'Another INVITE pending on this dialog.', severity: 'warn' },
  { code: 493, name: 'Undecipherable',          desc: 'Cannot decrypt S/MIME body.', severity: 'error' },
  // 5xx
  { code: 500, name: 'Internal Server Error',   desc: 'Server-side error — check system logs for details.', severity: 'error' },
  { code: 501, name: 'Not Implemented',         desc: 'Feature not implemented by this UA/server.', severity: 'error' },
  { code: 502, name: 'Bad Gateway',             desc: 'Upstream server error. Check trunk/carrier.', severity: 'error' },
  { code: 503, name: 'Service Unavailable',     desc: 'Server overloaded or down. Try again later.', severity: 'error' },
  { code: 504, name: 'Server Timeout',          desc: 'Upstream server timeout. Network or carrier issue.', severity: 'error' },
  { code: 505, name: 'Version Not Supported',   desc: 'SIP version not supported.', severity: 'error' },
  // 6xx
  { code: 600, name: 'Busy Everywhere',         desc: 'All user agents report busy.', severity: 'warn' },
  { code: 603, name: 'Decline',                 desc: 'Call was explicitly rejected by the user.', severity: 'warn' },
  { code: 604, name: 'Does Not Exist Anywhere', desc: 'User does not exist — check the called number.', severity: 'error' },
  { code: 606, name: 'Not Acceptable',          desc: 'UA refuses to accept the session parameters.', severity: 'error' },
];

const Q850_CAUSES: { code: number; name: string; desc: string; severity: 'info' | 'warn' | 'error' }[] = [
  { code:  1,  name: 'Unallocated Number',           desc: 'The called number has not been assigned. Wrong DID or number not ported.', severity: 'error' },
  { code:  3,  name: 'No Route to Destination',      desc: 'Network cannot route the call. Missing trunk or dialplan entry.', severity: 'error' },
  { code: 16,  name: 'Normal Call Clearing',          desc: 'Call ended normally by one party.', severity: 'info' },
  { code: 17,  name: 'User Busy',                    desc: 'Called party is on another call.', severity: 'warn' },
  { code: 18,  name: 'No User Responding',           desc: 'Ring timeout — no answer. Check timeout config.', severity: 'warn' },
  { code: 19,  name: 'No Answer from User',          desc: 'User is alerted but not answering.', severity: 'warn' },
  { code: 20,  name: 'Subscriber Absent',            desc: 'Mobile subscriber out of reach / not registered.', severity: 'error' },
  { code: 21,  name: 'Call Rejected',                desc: 'Call explicitly rejected. Check blacklist, ACL, or do-not-call rules.', severity: 'error' },
  { code: 22,  name: 'Number Changed',               desc: 'Called number has changed. Update routing.', severity: 'warn' },
  { code: 27,  name: 'Destination Out of Order',     desc: 'Remote UA is not functioning properly.', severity: 'error' },
  { code: 28,  name: 'Invalid Number Format',        desc: 'Called number format is invalid. Check dial patterns and prefix rules.', severity: 'error' },
  { code: 31,  name: 'Normal Unspecified',           desc: 'Generic normal call failure. Check logs for details.', severity: 'warn' },
  { code: 34,  name: 'No Circuit Available',         desc: 'No free channels on the trunk. Add more channels or check load.', severity: 'error' },
  { code: 38,  name: 'Network Out of Order',         desc: 'Network failure. Contact carrier.', severity: 'error' },
  { code: 41,  name: 'Temporary Failure',            desc: 'Transient network error. Retry. If persistent, check carrier status.', severity: 'error' },
  { code: 42,  name: 'Switching Equipment Congestion',desc: 'Carrier network congestion. Too many calls.', severity: 'error' },
  { code: 44,  name: 'Requested Circuit Unavailable',desc: 'Specific B-channel not available.', severity: 'error' },
  { code: 47,  name: 'Resource Unavailable',         desc: 'No resources available (channels, licenses, etc.).', severity: 'error' },
  { code: 50,  name: 'Facility Not Subscribed',      desc: 'Called feature is not part of your carrier plan.', severity: 'error' },
  { code: 52,  name: 'Outgoing Calls Barred',        desc: 'Outgoing calls are barred on this line/account.', severity: 'error' },
  { code: 54,  name: 'Incoming Calls Barred',        desc: 'Incoming calls are blocked on this number.', severity: 'error' },
  { code: 57,  name: 'Bearer Cap Not Available',     desc: 'Requested bearer capability temporarily unavailable.', severity: 'error' },
  { code: 65,  name: 'Bearer Cap Not Implemented',   desc: 'Codec or bearer type not supported. Check codecs.', severity: 'error' },
  { code: 81,  name: 'Invalid Call Reference',       desc: 'Call reference mismatch. Usually a signalling glitch.', severity: 'warn' },
  { code: 88,  name: 'Incompatible Destination',     desc: 'Codec/media incompatibility between endpoints. Check SDP.', severity: 'error' },
  { code: 95,  name: 'Invalid Message',              desc: 'Malformed SIP/ISDN message. Check SIP logs.', severity: 'error' },
  { code: 96,  name: 'Mandatory IE Missing',         desc: 'Required header/IE missing from message.', severity: 'error' },
  { code: 97,  name: 'Message Type Non-existent',    desc: 'Unrecognised message type.', severity: 'error' },
  { code: 102, name: 'Recovery on Timer Expiry',     desc: 'Timer T1/T2 expired — signalling timeout. Check NAT/firewall.', severity: 'error' },
  { code: 111, name: 'Protocol Error',               desc: 'Generic protocol error. Review full SIP trace.', severity: 'error' },
  { code: 127, name: 'Interworking Unspecified',     desc: 'Call failed at a gateway. Check trunk/carrier interoperability.', severity: 'error' },
];

// Map common call status strings to explanations
const STATUS_ANALYSIS: Record<string, { reason: string; actions: string[] }> = {
  'busy':       { reason: 'Called party was busy (on another call or DND enabled).', actions: ['Check if extension is in DND', 'Consider adding call waiting', 'Review queue fallover logic'] },
  'no-answer':  { reason: 'Call was not answered within the ring timeout.', actions: ['Check ring timeout setting', 'Verify voicemail is configured', 'Check if extension is registered'] },
  'failed':     { reason: 'Call could not be established due to a signalling or routing failure.', actions: ['Check trunk reachability (Options Ping)', 'Review system logs', 'Verify dialplan routing'] },
  'missed':     { reason: 'Inbound call was not answered.', actions: ['Enable voicemail or missed call notification', 'Review queue/IVR routing for unanswered calls'] },
  'abandoned':  { reason: 'Caller hung up while in queue before being answered.', actions: ['Review queue hold times', 'Add queue overflow or callback option', 'Check agent availability'] },
  'congestion': { reason: 'All trunk channels were busy or carrier rejected due to congestion.', actions: ['Check trunk capacity (max channels)', 'Contact carrier about congestion issues', 'Add redundant trunk'] },
  'chanunavail':{ reason: 'No suitable channel/trunk was available for the call.', actions: ['Verify trunk is registered', 'Check outbound routes in dialplan', 'Run OPTIONS ping on trunks'] },
};

// ── Helper components ─────────────────────────────────────────────────────────

const TAB_STYLE = (active: boolean) => ({
  padding: '8px 20px', borderRadius: '6px 6px 0 0',
  background: active ? 'rgba(139,92,246,0.2)' : 'transparent',
  border: 'none',
  borderBottom: active ? '2px solid #8b5cf6' : '2px solid transparent',
  color: active ? '#c4b5fd' : '#64748b',
  fontWeight: active ? 700 : 400,
  fontSize: 13, cursor: 'pointer',
});

function Severity({ s }: { s: 'info' | 'warn' | 'error' }) {
  const map = { info: ['#3b82f6','Info'], warn: ['#f59e0b','Warning'], error: ['#ef4444','Error'] };
  const [color, label] = map[s];
  return <span style={{ background: `${color}18`, color, borderRadius: 4, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>{label}</span>;
}

// ── Main component ────────────────────────────────────────────────────────────

export default function DiagnosticsPage({ user }: { user?: any }) {
  const [tab, setTab] = useState<'ping' | 'codes' | 'failures'>('ping');
  const [unlocked, setUnlocked]     = useState(false);
  const [debugCode, setDebugCode]   = useState('');
  const [debugError, setDebugError] = useState('');
  const [verifying, setVerifying]   = useState(false);

  const verifyDebugCode = async () => {
    if (!debugCode.trim()) return;
    setVerifying(true); setDebugError('');
    try {
      const r = await axios.post('/api/debug/verify', 
        { code: debugCode.trim().toUpperCase() },
        { headers: { Authorization: 'Bearer ' + (localStorage.getItem('ce_authToken') || '') } }
      );
      if (r.data?.valid) {
        setUnlocked(true);
        const exp = r.data.expires_at ? new Date(r.data.expires_at).getTime() : Date.now() + 7200000;
        if ((window as any).__ceEnableConsole) (window as any).__ceEnableConsole(exp);
      } else {
        setDebugError('Invalid or expired code.');
      }
    } catch (e: any) {
      setDebugError(e.response?.data?.error || 'Verification failed.');
    }
    setVerifying(false);
  };

  if (!unlocked) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#f1f5f9', marginBottom: 4 }}>Diagnostics</div>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>
          This page is restricted. Enter the debug access code provided by your administrator.
        </div>
        <div style={{ maxWidth: 340, background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 24 }}>
          <div style={{ fontSize: 24, textAlign: 'center', marginBottom: 12 }}>🔐</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0', marginBottom: 6, textAlign: 'center' }}>Debug Access Required</div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 16, textAlign: 'center' }}>
            Ask your admin to generate a code from Security → Debug Access
          </div>
          <input
            value={debugCode}
            onChange={e => setDebugCode(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && verifyDebugCode()}
            placeholder="Enter debug code..."
            maxLength={16}
            style={{ width: '100%', padding: '10px 12px', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 16, letterSpacing: 4, textAlign: 'center', boxSizing: 'border-box', outline: 'none', textTransform: 'uppercase' }}
          />
          {debugError && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 8, textAlign: 'center' }}>{debugError}</div>}
          <button
            onClick={verifyDebugCode}
            disabled={verifying || !debugCode.trim()}
            style={{ width: '100%', marginTop: 12, padding: '10px', background: '#7c3aed', border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 700, cursor: verifying ? 'not-allowed' : 'pointer', opacity: verifying ? 0.7 : 1 }}
          >
            {verifying ? 'Verifying…' : 'Unlock Diagnostics'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#f1f5f9' }}>Diagnostics</div>
        <span style={{ background: '#16a34a22', color: '#22c55e', border: '1px solid #22c55e44', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>🔓 Debug Unlocked</span>
      </div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 18 }}>
        Network tools, SIP/Q.850 code lookup, and call failure analysis
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid rgba(100,116,139,0.2)', marginBottom: 24 }}>
        <button style={TAB_STYLE(tab === 'ping')}     onClick={() => setTab('ping')}>🔍 Ping Tools</button>
        <button style={TAB_STYLE(tab === 'codes')}    onClick={() => setTab('codes')}>📖 Code Lookup</button>
        <button style={TAB_STYLE(tab === 'failures')} onClick={() => setTab('failures')}>⚠ Call Failures</button>
      </div>

      {tab === 'ping'     && <PingTools />}
      {tab === 'codes'    && <CodeLookup />}
      {tab === 'failures' && <CallFailures />}
    </div>
  );
}

// ── Ping Tools ────────────────────────────────────────────────────────────────

function PingTools() {
  const [icmpHost, setIcmpHost]     = useState('');
  const [icmpCount, setIcmpCount]   = useState('4');
  const [icmpResult, setIcmpResult] = useState<any>(null);
  const [icmpLoading, setIcmpLoading] = useState(false);

  const [aorName, setAorName]         = useState('');
  const [optResult, setOptResult]     = useState<any>(null);
  const [optLoading, setOptLoading]   = useState(false);

  const [err, setErr] = useState<string | null>(null);

  async function runPing() {
    if (!icmpHost.trim()) return;
    setIcmpLoading(true); setErr(null); setIcmpResult(null);
    try {
      const r = await (api as any).icmpPing(icmpHost.trim(), parseInt(icmpCount) || 4);
      setIcmpResult(r);
    } catch (e: any) { setErr(e?.response?.data?.error || 'Ping failed'); }
    finally { setIcmpLoading(false); }
  }

  async function runOptions() {
    if (!aorName.trim()) return;
    setOptLoading(true); setErr(null); setOptResult(null);
    try {
      const r = await (api as any).sipOptionsPing(aorName.trim());
      setOptResult(r);
    } catch (e: any) { setErr(e?.response?.data?.error || 'SIP OPTIONS failed'); }
    finally { setOptLoading(false); }
  }

  const IN = {
    padding: '9px 12px', borderRadius: 7, fontSize: 13,
    background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(100,116,139,0.3)',
    color: '#f1f5f9', outline: 'none',
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
      {err && (
        <div style={{ gridColumn: '1/-1', background: 'rgba(239,68,68,0.12)', border: '1px solid #ef444440',
          color: '#fca5a5', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>{err}</div>
      )}

      {/* ICMP Ping */}
      <div style={{ background: 'rgba(30,41,59,0.6)', border: '1px solid rgba(100,116,139,0.15)', borderRadius: 12, padding: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#f1f5f9', marginBottom: 4 }}>🏓 ICMP Ping</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
          Runs from the Telephony Engine — tests network reachability to the target
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input style={{ ...IN, flex: 2 }} placeholder="hostname or IP" value={icmpHost}
            onChange={e => setIcmpHost(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runPing()} />
          <input style={{ ...IN, width: 60 }} type="number" placeholder="4" value={icmpCount}
            onChange={e => setIcmpCount(e.target.value)} title="Packet count" />
          <button onClick={runPing} disabled={icmpLoading || !icmpHost.trim()} style={{
            background: 'linear-gradient(135deg,#8b5cf6,#6366f1)', border: 'none', color: 'white',
            borderRadius: 7, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            opacity: icmpLoading || !icmpHost.trim() ? 0.6 : 1,
          }}>{icmpLoading ? '…' : 'Ping'}</button>
        </div>

        {icmpResult && (
          <div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <div style={{ flex: 1, background: 'rgba(15,23,42,0.5)', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#64748b' }}>Status</div>
                <div style={{ fontWeight: 700, color: icmpResult.reachable ? '#10b981' : '#ef4444', marginTop: 3 }}>
                  {icmpResult.reachable ? '✓ Reachable' : '✗ Unreachable'}
                </div>
              </div>
              <div style={{ flex: 1, background: 'rgba(15,23,42,0.5)', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#64748b' }}>Packet Loss</div>
                <div style={{ fontWeight: 700, color: icmpResult.packet_loss > 0 ? '#f59e0b' : '#10b981', marginTop: 3 }}>
                  {icmpResult.packet_loss}%
                </div>
              </div>
              <div style={{ flex: 1, background: 'rgba(15,23,42,0.5)', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#64748b' }}>Avg RTT</div>
                <div style={{ fontWeight: 700, color: '#10b981', marginTop: 3 }}>
                  {icmpResult.rtt_avg != null ? `${icmpResult.rtt_avg} ms` : '—'}
                </div>
              </div>
            </div>
            <pre style={{
              background: 'rgba(0,0,0,0.4)', borderRadius: 6, padding: 12,
              fontSize: 11, color: '#94a3b8', overflowX: 'auto', whiteSpace: 'pre-wrap',
              maxHeight: 180, fontFamily: 'monospace',
            }}>{icmpResult.output}</pre>
          </div>
        )}
      </div>

      {/* SIP OPTIONS Ping */}
      <div style={{ background: 'rgba(30,41,59,0.6)', border: '1px solid rgba(100,116,139,0.15)', borderRadius: 12, padding: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#f1f5f9', marginBottom: 4 }}>⚡ SIP OPTIONS Ping</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
          Sends PJSIP <code style={{ color: '#a78bfa' }}>qualify aor</code> — checks if the Telephony Engine can reach the AOR
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input style={{ ...IN, flex: 1 }} placeholder="AOR name (e.g. pstn-trunk-aor)"
            value={aorName} onChange={e => setAorName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runOptions()} />
          <button onClick={runOptions} disabled={optLoading || !aorName.trim()} style={{
            background: 'linear-gradient(135deg,#0ea5e9,#3b82f6)', border: 'none', color: 'white',
            borderRadius: 7, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            opacity: optLoading || !aorName.trim() ? 0.6 : 1,
          }}>{optLoading ? '…' : 'Send'}</button>
        </div>

        {optResult && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { label: 'AOR', value: optResult.aor },
              { label: 'Status', value: optResult.pjsip_status, color: optResult.reachable ? '#10b981' : '#ef4444' },
              { label: 'RTT', value: optResult.rtt_ms != null ? `${optResult.rtt_ms.toFixed(1)} ms` : '—', color: '#10b981' },
              { label: 'Elapsed', value: `${optResult.elapsed_ms} ms` },
            ].map(f => (
              <div key={f.label} style={{ background: 'rgba(15,23,42,0.5)', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color: '#64748b' }}>{f.label}</div>
                <div style={{ fontWeight: 700, color: (f as any).color || '#e2e8f0', marginTop: 3, fontSize: 14 }}>{f.value}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 14, padding: 12, background: 'rgba(15,23,42,0.4)', borderRadius: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#8b5cf6', marginBottom: 6 }}>Common AOR Names</div>
          <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.7 }}>
            Trunks: <code style={{ color: '#94a3b8' }}>pstn-trunk-aor</code>,{' '}
            <code style={{ color: '#94a3b8' }}>teams-aor</code><br />
            Extensions: <code style={{ color: '#94a3b8' }}>1001</code>, <code style={{ color: '#94a3b8' }}>1002</code> (exact extension number)
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Code Lookup ───────────────────────────────────────────────────────────────

function CodeLookup() {
  const [codeTab, setCodeTab] = useState<'sip' | 'q850'>('sip');
  const [search, setSearch]   = useState('');

  const sipFiltered  = SIP_CODES.filter(c =>
    String(c.code).includes(search) || c.name.toLowerCase().includes(search.toLowerCase()) || c.desc.toLowerCase().includes(search.toLowerCase())
  );
  const q850Filtered = Q850_CAUSES.filter(c =>
    String(c.code).includes(search) || c.name.toLowerCase().includes(search.toLowerCase()) || c.desc.toLowerCase().includes(search.toLowerCase())
  );

  const list = codeTab === 'sip' ? sipFiltered : q850Filtered;

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['sip', 'q850'] as const).map(t => (
            <button key={t} onClick={() => setCodeTab(t)} style={{
              background: codeTab === t ? 'rgba(139,92,246,0.2)' : 'rgba(30,41,59,0.5)',
              border: `1px solid ${codeTab === t ? '#8b5cf6' : 'rgba(100,116,139,0.2)'}`,
              color: codeTab === t ? '#c4b5fd' : '#94a3b8',
              borderRadius: 6, padding: '6px 16px', fontSize: 12,
              fontWeight: codeTab === t ? 700 : 400, cursor: 'pointer',
            }}>
              {t === 'sip' ? 'SIP Response Codes' : 'Q.850 Cause Codes'}
            </button>
          ))}
        </div>
        <input
          placeholder="Search code or description…"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{
            flex: 1, padding: '7px 12px', borderRadius: 7, fontSize: 13,
            background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(100,116,139,0.3)',
            color: '#f1f5f9', outline: 'none',
          }} />
      </div>

      <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(100,116,139,0.15)', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'rgba(30,41,59,0.8)', borderBottom: '1px solid rgba(100,116,139,0.15)' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', width: 80 }}>Code</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', width: 220 }}>Name</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Explanation</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', width: 80 }}>Type</th>
            </tr>
          </thead>
          <tbody>
            {list.map((c, i) => (
              <tr key={c.code} style={{
                borderBottom: '1px solid rgba(100,116,139,0.08)',
                background: i % 2 === 0 ? 'transparent' : 'rgba(30,41,59,0.15)',
              }}>
                <td style={{ padding: '10px 14px', fontWeight: 800, color: '#a78bfa', fontFamily: 'monospace', fontSize: 15 }}>
                  {c.code}
                </td>
                <td style={{ padding: '10px 14px', fontWeight: 600, color: '#e2e8f0' }}>{c.name}</td>
                <td style={{ padding: '10px 14px', color: '#94a3b8', lineHeight: 1.5 }}>{c.desc}</td>
                <td style={{ padding: '10px 14px' }}><Severity s={c.severity} /></td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={4} style={{ padding: 30, textAlign: 'center', color: '#475569' }}>No results for "{search}"</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Call Failures ─────────────────────────────────────────────────────────────

function CallFailures() {
  const [calls, setCalls]     = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await (api as any).getRecentFailures();
      setCalls(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load call history');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  function getAnalysis(status: string) {
    const key = (status || '').toLowerCase().replace(/\s+/g, '-');
    return STATUS_ANALYSIS[key] || { reason: `Call ended with status: ${status}`, actions: ['Review system logs for details'] };
  }

  const STATUS_COLORS: Record<string, string> = {
    'busy': '#f59e0b', 'no-answer': '#f59e0b', 'failed': '#ef4444',
    'missed': '#64748b', 'abandoned': '#94a3b8', 'congestion': '#ef4444', 'chanunavail': '#ef4444',
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ fontSize: 14, color: '#94a3b8' }}>
          Recent non-answered calls — hover for failure analysis
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={load} disabled={loading} style={{
          background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(100,116,139,0.3)',
          color: '#94a3b8', borderRadius: 8, padding: '7px 14px', fontSize: 12, cursor: 'pointer',
        }}>{loading ? '…' : '↻ Refresh'}</button>
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid #ef444440', color: '#fca5a5',
          borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>{error}</div>
      )}

      <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(100,116,139,0.15)', borderRadius: 12, overflow: 'hidden' }}>
        {calls.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#475569' }}>
            {loading ? 'Loading…' : '✓ No failed calls found — all good!'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'rgba(30,41,59,0.8)', borderBottom: '1px solid rgba(100,116,139,0.15)' }}>
                {['Time', 'Direction', 'From', 'To', 'Status', 'Duration', 'Agent', ''].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {calls.map((c, i) => {
                const analysis = getAnalysis(c.status);
                const isOpen = expanded === c.id;
                const statusColor = STATUS_COLORS[c.status?.toLowerCase()] || '#64748b';
                return (
                  <>
                    <tr key={c.id} style={{
                      borderBottom: isOpen ? 'none' : '1px solid rgba(100,116,139,0.08)',
                      background: isOpen ? 'rgba(139,92,246,0.08)' : i % 2 === 0 ? 'transparent' : 'rgba(30,41,59,0.15)',
                      cursor: 'pointer',
                    }} onClick={() => setExpanded(isOpen ? null : c.id)}>
                      <td style={{ padding: '10px 12px', color: '#64748b', fontSize: 11, whiteSpace: 'nowrap' }}>
                        {c.call_start ? new Date(c.call_start).toLocaleString() : '—'}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontSize: 11, color: c.direction === 'inbound' ? '#3b82f6' : '#8b5cf6', fontWeight: 600, textTransform: 'capitalize' }}>
                          {c.direction === 'inbound' ? '↓ In' : '↑ Out'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', color: '#cbd5e1', fontFamily: 'monospace', fontSize: 12 }}>{c.caller_number || '—'}</td>
                      <td style={{ padding: '10px 12px', color: '#cbd5e1', fontFamily: 'monospace', fontSize: 12 }}>{c.callee_number || '—'}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ background: `${statusColor}18`, color: statusColor, border: `1px solid ${statusColor}40`, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                          {c.status}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', color: '#64748b', fontFamily: 'monospace' }}>
                        {c.duration != null ? `${c.duration}s` : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', color: '#94a3b8', fontSize: 12 }}>
                        {c.agent_name || c.username || '—'}
                      </td>
                      <td style={{ padding: '10px 12px', color: '#64748b', fontSize: 11 }}>
                        {isOpen ? '▲' : '▼'}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={`${c.id}-expand`} style={{ background: 'rgba(139,92,246,0.06)', borderBottom: '1px solid rgba(139,92,246,0.15)' }}>
                        <td colSpan={8} style={{ padding: '14px 20px' }}>
                          <div style={{ display: 'flex', gap: 20 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#8b5cf6', marginBottom: 6 }}>FAILURE REASON</div>
                              <div style={{ fontSize: 13, color: '#e2e8f0', lineHeight: 1.5 }}>{analysis.reason}</div>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#8b5cf6', marginBottom: 6 }}>RECOMMENDED ACTIONS</div>
                              <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {analysis.actions.map((a, idx) => (
                                  <li key={idx} style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.7 }}>{a}</li>
                                ))}
                              </ul>
                            </div>
                            {c.call_uuid && (
                              <div style={{ flex: 0.7 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#8b5cf6', marginBottom: 6 }}>CALL UUID</div>
                                <div style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace', wordBreak: 'break-all' }}>{c.call_uuid}</div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: '#334155' }}>
        {calls.length} failed/unanswered call{calls.length !== 1 ? 's' : ''} · click a row to expand analysis
      </div>
    </div>
  );
}
